import React, { useState, useRef, useEffect, useMemo } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { RealtimeTranscriber } from "assemblyai";
import * as RecordRTC from "recordrtc";
import Loader from "./loader";
import {
  generateClinicalNotes,
  generateGetDropdown,
  generateReferralLetter,
  getToken,
  transcribeAudio,
  uploadFileToServer,
} from "../components/ApiService";
import { toast } from "react-toastify";
import { Dropdown } from "../components/UI/Dropdown";
// import Markdown from "markdown-to-jsx";

function Main() {
  const [formData, setFormData] = useState({
    yourName: "",
    speciality: "",
    patientName: "",
    patientAge: "",
    referringDoctor: "",
  });
  const [audioUrl, setAudioUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [speakerTranscript, setSpeakerTranscript] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [isDoctorContentVisible, setIsDoctorContentVisible] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [audioSource, setAudioSource] = useState();
  const [errorMessages, setErrorMessages] = useState({
    yourName: "",
    speciality: "",
    patientName: "",
    patientAge: "",
    referringDoctor: "",
  });
  const [doctorData, setDoctorData] = useState();
  const [selectedOption, setSelectedOption] = useState(null);
  const timerRef = useRef(null);
  const realtimeTranscriber = useRef(null);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState({ id: null, data: [] });
  const recorder = useRef(null);
  const tooltipRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isSocketConnecting, setIsSocketConnecting] = useState(false);
  const options = useMemo(
    () => [
      { label: "Referral Letter", value: "Referral-Letter" },
      { label: "Post operative Letter", value: "Post-operative-Letter" },
      { label: "Follow-up Letter", value: "Follow-up-Letter" },
      {
        label: "Pre-Surgical Assessment Letter",
        value: "Pre-Surgical-Assessment-Letter",
      },
    ],
    []
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setCurrent({ id: null, data: [] });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tooltipRef]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "yourName" || name === "speciality") {
      const dataSaved = JSON.parse(localStorage.getItem("scrollOptions")) || [];
      const item = dataSaved.find((item) => item.id === name);

      if (item) {
        const lowerCaseValue = value.toLowerCase();
        const matchingSuggestions = item.data.filter((suggestion) =>
          suggestion.toLowerCase().includes(lowerCaseValue)
        );
        setCurrent({ id: name, data: matchingSuggestions });
      } else {
        setCurrent({ id: null, data: [] });
      }
    } else {
      setCurrent({ id: null, data: [] });
    }

    setErrorMessages((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (value.length <= 0) return;
    const lowerCaseValue = value.toLowerCase();

    let dataItem = JSON.parse(localStorage.getItem("scrollOptions")) || [];
    const existingItemIndex = dataItem.findIndex((item) => item.id === name);

    if (existingItemIndex !== -1) {
      const existingItem = dataItem[existingItemIndex];
      if (
        !existingItem.data.some(
          (existingValue) => existingValue.toLowerCase() === lowerCaseValue
        )
      ) {
        existingItem.data.push(value);
      }
    } else {
      dataItem.push({
        id: name,
        data: [value],
      });
    }
    localStorage.setItem("scrollOptions", JSON.stringify(dataItem));
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData((prevData) => ({
      ...prevData,
      [current.id]: suggestion,
    }));
    setCurrent({ id: null, data: [] });
  };
  // const CustomLink = ({ children, ...props }) => {
  //   return (
  //     <a
  //       {...props}
  //       className="text-[#0645AD]"
  //       target="_blank"
  //       rel="noopener noreferrer"
  //     >
  //       {children}
  //     </a>
  //   );
  // };
  const validateForm = () => {
    const errors = {};
    const { yourName, speciality, patientName, patientAge, referringDoctor } =
      formData;
    if (!yourName) errors.yourName = "Please fill in your name.";
    if (!speciality) errors.speciality = "Please fill in your specialty.";
    if (!patientName) errors.patientName = "Please fill in the patient name.";
    if (!patientAge) errors.patientAge = "Please fill in the patient age.";
    if (!referringDoctor)
      errors.referringDoctor = "Please fill in the referring doctor.";
    if (Object.keys(errors).length > 0) {
      toast.error("All fields are required");
      setErrorMessages(errors);
      return false;
    }
    return true;
  };

  const handleDownloadClinicalNotesPDF = (letterheadType) => {
    const doc = new jsPDF();

    let imgMaxHeight = 0;
    let margin = 0;
    let letterheadTop = "";
    let letterheadBottom = "";
    let fileName = "";

    // Sydney Norwest
    if (letterheadType === 1) {
      letterheadTop = "/images/sydney-norwest-top.png";
      letterheadBottom = "/images/sydney-norwest-bottom.png";
      fileName = "clinical_notes_sydney-norwest.pdf";
      imgMaxHeight = 38;
      margin = 10;
    }

    // ReSet
    if (letterheadType === 2) {
      letterheadTop = "/images/reSet-top.png";
      letterheadBottom = "/images/reSet-bottom.png";
      fileName = "clinical_notes_reSet.pdf";
      imgMaxHeight = 38;
      margin = 10;
    }

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    // const imgY = margin;

    const addLetterhead = (doc) => {
      // Add header and footer images
      const imgWidth = doc.internal.pageSize.width;
      const imgY = margin;
      doc.addImage(letterheadTop, "PNG", 0, imgY, imgWidth, imgMaxHeight);
      doc.addImage(
        letterheadBottom,
        "PNG",
        0,
        pageHeight - (imgMaxHeight + margin / 2),
        imgWidth,
        imgMaxHeight - 3
      );
    };

    const formatText = (text) => {
      // Remove HTML tags and format the text to handle bold sections
      const plainText = text.replace(/<\/?[^>]+(>|$)/g, "").replace(/\*/g, "");
      return plainText.split("\n").map((line) => {
        return { text: line, bold: line.trim().endsWith(":") };
      });
    };

    const img = new Image();
    img.src = letterheadTop;

    img.onload = () => {
      doc.setFontSize(12);

      const text = clinicalNotes || "";
      const formattedLines = formatText(text);
      const lineHeight = 10;

      // Calculate available space for content and initial Y position
      const maxContentHeight = pageHeight - imgMaxHeight;
      let currentY = imgMaxHeight + margin + 20;

      // Add the first page's letterhead
      addLetterhead(doc);

      formattedLines.forEach(({ text, bold }) => {
        if (bold) {
          doc.setFont("Helvetica", "bold");
        } else {
          doc.setFont("Helvetica", "normal");
        }

        const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);

        lines.forEach((line) => {
          if (currentY + lineHeight > maxContentHeight) {
            doc.addPage();
            addLetterhead(doc); // Re-add header and footer for the new page
            currentY = imgMaxHeight + margin + 20; // Reset Y position for new page
          }
          doc.text(line, margin, currentY);
          currentY += lineHeight;
        });
      });

      doc.save(fileName);
    };

    img.onerror = () => {
      console.error("Failed to load image.");
    };
  };

  const handleDownloadDoctorsLetterPDF = (letterheadType) => {
    const doc = new jsPDF();

    let imgMaxHeight = 0;
    let margin = 0;
    let letterheadTop = "";
    let letterheadBottom = "";
    let fileName = "";

    // Sydney Norwest
    if (letterheadType === 1) {
      letterheadTop = "/images/sydney-norwest-top.png";
      letterheadBottom = "/images/sydney-norwest-bottom.png";
      fileName = "doctors_letter_sydney-norwest.pdf";
      imgMaxHeight = 42;
      margin = 15;
    }

    // ReSet
    if (letterheadType === 2) {
      letterheadTop = "/images/reSet-top.png";
      letterheadBottom = "/images/reSet-bottom.png";
      fileName = "doctors_letter_reSet.pdf";
      imgMaxHeight = 35;
      margin = 10;
    }

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const addLetterhead = (doc) => {
      // Add header and footer images
      const imgWidth = doc.internal.pageSize.width;
      const imgY = margin;
      doc.addImage(letterheadTop, "PNG", 0, imgY, imgWidth, imgMaxHeight);
      doc.addImage(
        letterheadBottom,
        "PNG",
        0,
        pageHeight - (imgMaxHeight + margin),
        imgWidth,
        imgMaxHeight
      );
    };

    const img = new Image();
    img.src = letterheadTop;

    img.onload = () => {
      doc.setFontSize(12);

      const text = doctorData || "";
      let lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
      const defaultLineHeight = doc.getTextDimensions(lines[0]).h;
      const reducedLineHeight = defaultLineHeight * 1.5;

      // Calculate the maximum content height to avoid overlapping with the footer
      const maxContentHeight = pageHeight - imgMaxHeight * 2;
      let y = imgMaxHeight + margin + 20;

      // Add the first page's letterhead
      addLetterhead(doc);

      lines.forEach((line, index) => {
        if (y + reducedLineHeight > maxContentHeight) {
          doc.addPage();
          addLetterhead(doc); // Re-add header and footer for the new page
          y = imgMaxHeight + margin + 20; // Reset position for the new page
        }
        doc.text(line, margin, y);
        y += reducedLineHeight;
      });

      doc.save(fileName);
    };

    img.onerror = () => {
      console.error("Failed to load image.");
    };
  };

  const handleDownloadSpeakerPDF = () => {
    const doc = new jsPDF();
    const imageUrl = "/images/Clinical.jpg";
    const imgMaxHeight = 15;
    const margin = 10;

    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;
      const aspectRatio = imgWidth / imgHeight;
      let imgHeightScaled = imgMaxHeight;
      let imgWidthScaled = imgHeightScaled * aspectRatio;
      const maxWidth = doc.internal.pageSize.width - margin * 2;
      if (imgWidthScaled > maxWidth) {
        imgWidthScaled = maxWidth;
        imgHeightScaled = imgWidthScaled / aspectRatio;
      }

      const imgX = (doc.internal.pageSize.width - imgWidthScaled) / 2;
      const imgY = margin;
      doc.addImage(img, "JPEG", imgX, imgY, imgWidthScaled, imgHeightScaled);
      doc.setFontSize(16);
      const transcriptContent = speakerTranscript || "";
      const lines = doc.splitTextToSize(transcriptContent, 180);
      let y = imgY + imgHeightScaled + margin + 10;
      for (let i = 0; i < lines.length; i++) {
        if (y > 280) {
          doc.addPage();
          y = 10;
        }
        doc.text(lines[i], 10, y);
        y += 10;
      }
      doc.save("speaker_transcript.pdf");
    };

    img.onerror = () => {
      console.error("Failed to load image.");
    };
  };

  // const handleDownloadPDF = (letterheadType, downloadType) => {
  //   const doc = new jsPDF();

  //   let imgMaxHeight = 0;
  //   let margin = 0;
  //   let letterheadTop = "";
  //   let letterheadBottom = "";

  //   // Sydney Norwest
  //   if (letterheadType === 1) {
  //     letterheadTop = "/images/sydney-norwest-top.png";
  //     letterheadBottom = "/images/sydney-norwest-bottom.png";
  //     imgMaxHeight = 42;
  //     margin = 15;
  //   }

  //   // ReSet
  //   if (letterheadType === 2) {
  //     letterheadTop = "/images/Clinical.jpg";
  //     // letterheadTop = "/images/reSet-top.png";
  //     letterheadBottom = "/images/reSet-bottom.png";
  //     imgMaxHeight = 35;
  //     margin = 10;
  //   }

  //   const imgHeight = letterheadTop.naturalHeight;
  //   const imgWidth = (letterheadTop.naturalWidth * imgMaxHeight) / imgHeight;
  //   const imgX = (doc.internal.pageSize.width - imgWidth) / 2;

  //   const addLetterhead = (doc) => {
  //     // Add header and footer images
  //     const imgWidth = doc.internal.pageSize.width;
  //     const imgY = margin;
  //     doc.addImage(letterheadTop, "PNG", imgX, imgY, imgWidth, imgMaxHeight);

  //     // doc.addImage(letterheadTop, "PNG", 0, imgY, imgWidth, imgMaxHeight);
  //     // doc.addImage(
  //     //   letterheadBottom,
  //     //   "PNG",
  //     //   0,
  //     //   doc.internal.pageSize.height - (imgMaxHeight + margin),
  //     //   imgWidth,
  //     //   imgMaxHeight
  //     // );
  //   };

  //   const letterhead = new Image();
  //   letterhead.src = letterheadTop;
  //   letterhead.onload = () => {
  //     doc.setFontSize(14);

  //     let transcriptContent = "";
  //     if (downloadType === 1) transcriptContent = transcript;
  //     if (downloadType === 2) transcriptContent = speakerTranscript;

  //     const lines = doc.splitTextToSize(transcriptContent, 180);
  //     let y = imgMaxHeight + margin + 20;

  //     // Calculate the maximum height for content to avoid overlapping with the footer
  //     const pageHeight = doc.internal.pageSize.height;
  //     // const maxContentHeight = pageHeight - imgMaxHeight * 2;
  //     const maxContentHeight = pageHeight - margin * 2;

  //     // Add the first page's letterhead and content
  //     addLetterhead(doc);

  //     for (let i = 0; i < lines.length; i++) {
  //       if (y > maxContentHeight) {
  //         doc.addPage();
  //         addLetterhead(doc); // Re-add header and footer for new page
  //         y = imgMaxHeight + margin + 20; // Reset position for new page
  //       }
  //       doc.text(lines[i], 10, y);
  //       y += 10;
  //     }

  //     doc.save("Live Transcript.pdf");
  //   };
  // };

  const resetForm = () => {
    // Reset all form fields and relevant states
    setFormData({
      yourName: "",
      speciality: "",
      patientName: "",
      patientAge: "",
      referringDoctor: "",
    });

    // Reset all audio-related states
    setAudioUrl(null);
    setIsRecording(false);
    setIsPaused(false); // Reset pause state
    setUploadProgress(0);
    setIsUploading(false);
    setIsContentVisible(false);

    // Reset transcription and text states
    setSpeakerTranscript("");
    setClinicalNotes("");
    setDoctorData("");
    setTranscript("");

    // Reset additional visibility and timing states
    setIsDoctorContentVisible(false);
    setRecordingTime(0);

    // Close any active recorder/transcription sessions if applicable
    if (realtimeTranscriber.current) {
      realtimeTranscriber.current.close();
      realtimeTranscriber.current = null;
    }

    if (recorder.current) {
      recorder.current.reset(); // Ensure recorder state is reset if paused
      recorder.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current); // Clear the recording timer
      timerRef.current = null;
    }
  };

  // Call this function on page load or component mount
  const preloadMicrophone = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error("Microphone access denied", err);
    }
  };

  useEffect(() => {
    preloadMicrophone(); // Preload microphone permissions on mount
  }, []);

  const startTranscription = async () => {
    setIsRecording(true);
    setIsFileUploaded(false);
    setIsSocketConnecting(true); // Start the loading state

    const token = await getToken();
    if (!token) return;

    realtimeTranscriber.current = new RealtimeTranscriber({
      token,
      sampleRate: 16_000,
    });

    const texts = {};
    let isConnected = false; // Flag to ensure socket is connected

    realtimeTranscriber.current.on("transcript", (transcript) => {
      let msg = "";
      texts[transcript.audio_start] = transcript.text;
      const keys = Object.keys(texts);
      keys.sort((a, b) => a - b);
      for (const key of keys) {
        if (texts[key]) {
          msg += ` ${texts[key]}\n`;
        }
      }
      setTranscript(msg);
    });

    realtimeTranscriber.current.on("error", (event) => {
      console.error(event);
      realtimeTranscriber.current.close();
      realtimeTranscriber.current = null;
      setIsSocketConnecting(false); // End loading on error
    });

    realtimeTranscriber.current.on("close", (code, reason) => {
      realtimeTranscriber.current = null;
      setIsSocketConnecting(false); // End loading on close
    });

    // Set flag when connection is successfully opened and hide loading spinner
    realtimeTranscriber.current.on("open", () => {
      isConnected = true;
      setIsSocketConnecting(false); // End loading when connection is ready
    });

    await realtimeTranscriber.current.connect();

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        recorder.current = new RecordRTC(stream, {
          type: "audio",
          mimeType: "audio/webm;codecs=pcm",
          recorderType: RecordRTC.StereoAudioRecorder,
          timeSlice: 250,
          desiredSampRate: 16000,
          numberOfAudioChannels: 1,
          bufferSize: 4096,
          audioBitsPerSecond: 128000,
          ondataavailable: async (blob) => {
            if (!realtimeTranscriber.current || !isConnected) {
              console.warn("WebSocket not ready, skipping audio.");
              return;
            }
            const buffer = await blob.arrayBuffer();
            realtimeTranscriber.current.sendAudio(buffer);
          },
        });
        recorder.current.startRecording();

        timerRef.current = setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 1);
        }, 1000);
      })
      .catch((err) => {
        console.error(err);
        setIsSocketConnecting(false); // End loading on error
      });
  };

  const pauseTranscription = () => {
    if (recorder.current && !isPaused) {
      recorder.current.pauseRecording();
      setIsPaused(true);

      if (timerRef.current) {
        clearInterval(timerRef.current); // Stop the timer when paused
      }
    }
  };

  const resumeTranscription = () => {
    if (recorder.current && isPaused) {
      recorder.current.resumeRecording();
      setIsPaused(false);

      // Resume the timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  const endTranscription = async () => {
    setIsRecording(false);
    handleGetDropdownData();
    if (recorder.current) {
      recorder.current.stopRecording(async () => {
        const audioBlob = recorder.current.getBlob();
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl);
        await uploadFileToServer(audioBlob, getAudioSource);
        URL.revokeObjectURL(audioUrl);

        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      });
    }

    if (realtimeTranscriber.current) {
      await realtimeTranscriber.current.close();
      realtimeTranscriber.current = null;
    }
  };

  const getAudioSource = async (audio) => {
    setAudioSource(audio);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    const maxFileSize = 100 * 1024 * 1024;

    if (file) {
      if (file.size > maxFileSize) {
        alert("File size exceeds the maximum limit of 100MB.");
        return;
      }

      const audioUrl = URL.createObjectURL(file);
      setAudioUrl(audioUrl);
      setIsFileUploaded(true);
      setLoading(true);

      try {
        await uploadFileToServer(file, getAudioSource);
      } catch (error) {
        console.error("File upload failed:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTranscription = async () => {
    setLoading(true);
    try {
      const { speakerTranscript } = await transcribeAudio(audioSource);
      setSpeakerTranscript(speakerTranscript);
      handleGetDropdownData(speakerTranscript);
    } catch (error) {
      console.error("Error during transcription:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleGenerateClinicalNotes = async () => {
    const finalTranscript = speakerTranscript || transcript;
    if (!finalTranscript) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const bodyData = {
        doctor_name: formData.yourName,
        doctor_specialty: formData.speciality,
        patient_name: formData.patientName,
        patient_age: formData.patientAge,
        referring_doctor_name: formData.referringDoctor,
        input_data: finalTranscript,
      };
      const result = await generateClinicalNotes(bodyData);
      setClinicalNotes(result?.clinical_notes);
      setIsContentVisible(true);
    } catch (error) {
      console.error("Error during clinical notes generation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReferralLetter = async () => {
    const finalTranscript = speakerTranscript || transcript;
    if (!finalTranscript) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const bodyData = {
        doctor_name: formData.yourName,
        doctor_specialty: formData.speciality,
        patient_name: formData.patientName,
        patient_age: formData.patientAge,
        referring_doctor_name: formData.referringDoctor,
        input_data: finalTranscript,
        letter_type: selectedOption?.value || options?.[0]?.value,
      };
      const result = await generateReferralLetter(bodyData);
      const formattedResult = result?.letter?.startsWith("Letter:")
        ? result?.letter?.replace("Letter:", "").trim()
        : result?.letter;

      setDoctorData(formattedResult);
      setIsDoctorContentVisible(true);
    } catch (error) {
      console.error("Error during referral letter generation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetDropdownData = async (text) => {
    const finalTranscript = text ?? speakerTranscript ?? transcript;
    if (!finalTranscript) return;
    setLoading(true);
    try {
      const bodyData = {
        input_data: finalTranscript,
      };
      const result = await generateGetDropdown(bodyData);
      setSelectedOption(
        options?.find((item) => item?.value === result?.letter)
      );
    } catch (error) {
      console.error("Error during referral letter generation:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-2 lg:p-6">
      <div className="flex justify-center mb-8 mt-8">
        <img
          src="/images/LogoMain.svg"
          alt="Mednotes Logo"
          className="lg:w-[20%] sm:w-full md:w-[30%] "
        />
      </div>
      <div className="bg-white shadow-lg rounded-lg max-w-[1123px] mx-auto lg:p-8 p-4">
        <h2 className="text-2xl font-light mb-6">
          Specialist Notes & Reporting
        </h2>
        <div className="space-y-4 bg-[#F9FAFB] rounded-lg w-full p-2">
          {[
            {
              label: "Your Name",
              name: "yourName",
              type: "text",
            },
            {
              label: "Speciality",
              name: "speciality",
              type: "text",
            },
            {
              label: "Patient Name",
              name: "patientName",
              type: "text",
            },
            {
              label: "Patient Age",
              name: "patientAge",
              type: "number",
            },
            {
              label: "Referring Doctor",
              name: "referringDoctor",
              type: "text",
            },
          ].map(({ label, name, type }) => (
            <div key={name} className="relative">
              <label className="block text-gray-700 mb-1">{label}</label>
              <input
                type={type}
                name={name}
                value={formData[name] || ""}
                onChange={(e) => {
                  if (type === "number") {
                    const value = e.target.value;
                    if (value < 0) return;
                    setFormData((prevFormData) => ({
                      ...prevFormData,
                      [name]: value,
                    }));
                  } else {
                    handleChange(e);
                  }
                }}
                onBlur={handleBlur}
                onWheel={(e) => type === "number" && e.target.blur()}
                onPaste={(e) => {
                  if (type === "number") {
                    const pastedValue = e.clipboardData.getData("Text");
                    if (pastedValue < 0) {
                      e.preventDefault();
                    }
                  }
                }}
                autoComplete="off"
                className="w-full border border-gray-300 rounded-md p-2"
                placeholder={`Enter ${label.toLowerCase()}`}
                required
              />
              {errorMessages[name] && (
                <div className="text-red-600 mt-1">{errorMessages[name]}</div>
              )}
              {current.id === name && current.data.length > 0 && (
                <ul
                  ref={tooltipRef}
                  className="absolute z-10 bg-black border left-20  w-auto text-white border-gray-300 mt-1 rounded-lg shadow-md h-auto overflow-auto"
                >
                  {current.data.map((suggestion, index) => (
                    <li
                      key={index}
                      className="cursor-pointer text-white p-2 hover:bg-gray-700"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8">
          {isUploading ? (
            <>
              <h2 className="text-xl font-semibold mb-4">Upload File</h2>
              <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between max-w-[400px]">
                <div className="flex items-start">
                  <img
                    src="/images/document.svg"
                    alt="File Icon"
                    className="h-6 w-6 mr-3"
                  />
                  <div>
                    <p className="text-gray-800">HannahBusing_Audio.mp3</p>
                    <p className="text-sm text-gray-500">200 KB</p>
                    {isUploading ? (
                      uploadProgress < 100 ? (
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className="bg-blue-500 h-2.5 rounded-full"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-gray-800">{uploadProgress}%</p>
                        </div>
                      ) : (
                        <div className="flex justify-between gap-2">
                          <img
                            src="/images/uploadSound.svg"
                            alt="Upload Complete"
                            className="h-6 w-48"
                          />
                          <img
                            src="/images/Pause.svg"
                            alt="Pause"
                            className="h-6 w-48"
                          />
                        </div>
                      )
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center">
                  <button
                    onClick={resetForm}
                    className="ml-4 text-red-600 mt-[-40px]"
                  >
                    <img
                      src="/images/delete.svg"
                      alt="Delete Icon"
                      className="h-5 w-5"
                    />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="mt-8">
              {!isRecording ? (
                !audioUrl && (
                  <>
                    <h2 className="text-xl font-semibold mb-4">Upload File</h2>
                    <div className="border-2 border-dashed border-[#30bd7e] p-6 rounded-lg text-center bg-[#F9FAFB]">
                      <label className="cursor-pointer">
                        <img
                          src={"/images/upload.svg"}
                          alt="Upload Icon"
                          className="mx-auto mb-4"
                        />
                        <input
                          type="file"
                          className="hidden"
                          accept=".mp3, .m4a, .wav"
                          onChange={handleFileUpload}
                        />
                        <p className="text-[#30bd7e]">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-gray-500">
                          Up to 100MB max .mp3, .wav
                        </p>
                      </label>
                      <button
                        className="mt-4 bg-[#30bd7e] text-white px-4 py-2 rounded-md transition-colors"
                        onClick={startTranscription}
                      >
                        Start recording
                      </button>
                    </div>
                  </>
                )
              ) : isSocketConnecting ? ( // Show loader while WebSocket is connecting
                <div className="flex items-center justify-center">
                  <img
                    className="h-[100px]"
                    src="/images/loader.gif"
                    alt="Loading..."
                  />
                  <p className="ml-2 text-gray-500">
                    <div className="pulse text-center">
                      <h2 className="text-2xl font-semibold">
                        Connecting to the transcription service...
                      </h2>
                    </div>
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-semibold mb-4">Recording</h2>
                  <div className="flex items-center border border-gray-300 w-full rounded-full p-4">
                    <div className="flex items-center">
                      <img
                        src="/images/time.svg"
                        alt="Recording Icon"
                        className="w-4 h-4"
                      />
                      <p className="ml-2 font-semibold text-gray-800">
                        {new Date(recordingTime * 1000)
                          .toISOString()
                          .substr(11, 8)}
                      </p>
                    </div>

                    <p className="flex-grow flex-col ml-4 text-gray-500">
                      {isPaused
                        ? "Recording paused"
                        : "Audio recording in progress"}
                    </p>

                    {/* Pause/Resume Button */}
                    <button
                      className="flex items-center justify-center mx-2"
                      onClick={
                        isPaused ? resumeTranscription : pauseTranscription
                      }
                    >
                      <img
                        src={
                          isPaused ? "/images/play.svg" : "/images/pause.svg"
                        }
                        alt={isPaused ? "Resume Button" : "Pause Button"}
                        className="w-6 h-6"
                      />
                    </button>

                    {/* Stop Button */}
                    <button
                      className="flex items-center justify-center"
                      onClick={endTranscription}
                    >
                      <img
                        src="/images/stop.svg"
                        alt="Stop Button"
                        className="w-6 h-6"
                      />
                    </button>
                  </div>
                </>
              )}

              {audioUrl && !isUploading && (
                <>
                  <h2 className="text-xl font-semibold mb-4 mt-2">
                    Uploaded File
                  </h2>
                  <div className="border border-gray-300 rounded-lg p-4 flex items-center justify-between max-w-[400px]">
                    <div className="flex items-start">
                      <div>
                        <div className="mt-2 w-full">
                          <audio controls src={audioUrl} className="flex-1" />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <button onClick={resetForm} className="ml-4 text-red-600">
                        <img
                          src="/images/delete.svg"
                          alt="Delete Icon"
                          className="h-8 w-8"
                        />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {!isUploading && (
          <>
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Live Transcript</h2>
              <textarea
                className="w-full border border-gray-300 rounded-md p-4 h-36"
                onChange={(e) => setTranscript(e.target.value)}
                value={transcript || ""}
                disabled={isFileUploaded}
                placeholder="Click on start recording for live transcript"
              ></textarea>
            </div>
            {/* <div className="flex justify-center lg:justify-end items-center mt-8 mb-4">
              <button
                className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100 mr-3"
                onClick={() => handleDownloadPDF(1, 1)}
                disabled={!transcript}
              >
                <img
                  src="/images/download.svg"
                  alt="Download Icon"
                  className="h-5 w-5"
                />
                <span className="text-[#30bd7e]">
                  <span>Download PDF</span>
                  <br />
                  <small>Sydney Norwest</small>
                </span>
              </button>
              <button
                className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100"
                onClick={() => handleDownloadPDF(2, 1)}
                disabled={!transcript}
              >
                <img
                  src="/images/download.svg"
                  alt="Download Icon"
                  className="h-5 w-5"
                />
                <span className="text-[#30bd7e]">
                  <span>Download PDF</span>
                  <br />
                  <small>ReSet</small>
                </span>
              </button>
            </div> */}
          </>
        )}

        {audioUrl && !isUploading && (
          <>
            <div className="mt-8">
              <div className="flex justify-between">
                <h2 className="text-xl font-semibold mb-4">Audio Transcript</h2>
                <button
                  onClick={handleTranscription}
                  className="mb-4 px-4 py-2 bg-[#30bd7e] text-white rounded-md shadow-2xl"
                  style={{ boxShadow: "5px 5px 7px rgba(0, 0, 0, 0.2)" }}
                >
                  Start Transcription
                </button>
              </div>

              <div className="w-full border border-gray-300 rounded-md relative min-h-36">
                {loading ? (
                  <Loader />
                ) : (
                  <textarea
                    className="w-full h-full p-4 "
                    value={speakerTranscript}
                    placeholder="Click on start recording for live transcript"
                    rows={5}
                    onChange={(e) => setSpeakerTranscript(e.target.value)}
                  ></textarea>
                )}
              </div>
            </div>
            {speakerTranscript && (
              <div className="flex justify-center lg:justify-end items-center mt-8 mb-4">
                <button
                  className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100"
                  onClick={() => {
                    handleDownloadSpeakerPDF();
                  }}
                  disabled={!speakerTranscript}
                >
                  <img
                    src="/images/download.svg"
                    alt="Download Icon"
                    className="h-5 w-5"
                  />
                  <span className="text-[#30bd7e]">
                    <span>Download PDF</span>
                  </span>
                </button>
                {/* <button
                className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100 mr-3"
                onClick={() => {
                  handleDownloadPDF(1, 2);
                }}
                disabled={!speakerTranscript}
              >
                <img
                  src="/images/download.svg"
                  alt="Download Icon"
                  className="h-5 w-5"
                />
                <span className="text-[#30bd7e]">
                  <span>Download PDF</span>
                  <br />
                  <small>Sydney Norwest</small>
                </span>
              </button>
              <button
                className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100"
                onClick={() => {
                  handleDownloadPDF(2, 2);
                }}
                disabled={!speakerTranscript}
              >
                <img
                  src="/images/download.svg"
                  alt="Download Icon"
                  className="h-5 w-5"
                />
                <span className="text-[#30bd7e]">
                  <span>Download PDF</span>
                  <br />
                  <small>ReSet</small>
                </span>
              </button> */}
              </div>
            )}
          </>
        )}

        <div>
          {/* Clinic Notes End*/}
          {(transcript || speakerTranscript) && (
            <>
              <h2 className="text-xl font-semibold mb-6">
                Generate Clinical Notes
              </h2>
              {
                <div className="mt-4 bg-[#F9FAFB] rounded-lg flex flex-col items-center lg:items-start !w-full">
                  <p className="mb-2">
                    To generate the Clinical Notes, Click the button below
                  </p>
                  <button
                    className="text-black px-4 py-2 mb-4 rounded-md border border-gray-400 flex items-center space-x-2 hover:bg-gray-100"
                    onClick={() => handleGenerateClinicalNotes()}
                  >
                    <img src="/images/plus.svg" alt="Plus Icon" />
                    <span className="text-[#30bd7e]">
                      Generate Clinical Notes
                    </span>
                  </button>
                </div>
              }
              {isContentVisible && (
                <div>
                  <div className="mt-8 mb-8">
                    <p className="mb-2">
                      To generate the Clinical Notes, press down the button
                    </p>
                    {/* <div className="markdownwrapper   w-fit whitespace-pre-line break-words font-normal text-sm leading-normal text-black">
                      <Markdown
                        options={{
                          overrides: {
                            a: {
                              component: CustomLink,
                            },
                          },
                        }}
                      >
                        {clinicalNotes}
                      </Markdown>
                    </div> */}
                    <div className="w-full font-normal text-sm leading-normal text-black">
                      <textarea
                        className="w-full h-full p-[10px] whitespace-pre-line break-words rounded-[10px] border border-solid border-[#ccc]"
                        value={clinicalNotes}
                        placeholder=""
                        rows={6}
                        onChange={(e) => setClinicalNotes(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                  <div className="flex justify-center lg:justify-end items-center mt-8 mb-4">
                    <button
                      className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100 mr-3"
                      onClick={() => {
                        handleDownloadClinicalNotesPDF(1);
                      }}
                    >
                      <img
                        src="/images/download.svg"
                        alt="Download Icon"
                        className="h-5 w-5"
                      />
                      <span className="text-[#30bd7e]">
                        <span>Download PDF</span>
                        <br />
                        <small>Sydney Norwest</small>
                      </span>
                    </button>
                    <button
                      className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100"
                      onClick={() => {
                        handleDownloadClinicalNotesPDF(2);
                      }}
                    >
                      <img
                        src="/images/download.svg"
                        alt="Download Icon"
                        className="h-5 w-5"
                      />
                      <span className="text-[#30bd7e]">
                        <span>Download PDF</span>
                        <br />
                        <small>ReSet</small>
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Clinic Notes End*/}

          {/* Doctor's Letter Start*/}
          {(transcript || speakerTranscript) && (
            <>
              <h2 className="text-xl font-semibold mb-6">
                Generate Doctor's Letter
              </h2>
              {
                <div className="mt-4 bg-[#F9FAFB] rounded-lg flex flex-col items-center lg:items-start !w-full">
                  <p className="mb-2">
                    To generate a letter to the referring Doctor, Click the
                    button below
                  </p>
                  <div className="gap-3 flex w-full">
                    <Dropdown
                      options={options}
                      selectedValue={selectedOption}
                      onChange={setSelectedOption}
                    />
                    <button
                      className="text-black px-4 py-2 mb-4 rounded-md border border-gray-400 flex items-center space-x-2 hover:bg-gray-100"
                      onClick={() =>
                        handleGenerateReferralLetter(/* pass your speakerTranscript here */)
                      }
                    >
                      <img src="/images/plus.svg" alt="Plus Icon" />
                      <span className="text-[#30bd7e]">Generate Letter</span>
                    </button>
                  </div>
                </div>
              }
              {isDoctorContentVisible && (
                <div>
                  <div className="mt-8">
                    <p className="mb-2">
                      To generate the doctor’s letter, press down the button
                    </p>
                    <div className="w-full font-normal text-sm leading-normal text-black">
                      <textarea
                        className="w-full h-full p-[10px] whitespace-pre-line break-words rounded-[10px] border border-solid border-[#ccc]"
                        value={doctorData}
                        placeholder=""
                        rows={6}
                        onChange={(e) => setDoctorData(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                  <div className="flex justify-center lg:justify-end items-center mt-8 mb-4">
                    <button
                      className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100 mr-3"
                      onClick={() => {
                        handleDownloadDoctorsLetterPDF(1);
                      }}
                    >
                      <img
                        src="/images/download.svg"
                        alt="Download Icon"
                        className="h-5 w-5"
                      />
                      <span className="text-[#30bd7e]">
                        <span>Download PDF</span>
                        <br />
                        <small>Sydney Norwest</small>
                      </span>
                    </button>
                    <button
                      className="text-black px-4 py-2 rounded-md border border-[#30bd7e] flex items-center space-x-2 hover:bg-gray-100"
                      onClick={() => {
                        handleDownloadDoctorsLetterPDF(2);
                      }}
                    >
                      <img
                        src="/images/download.svg"
                        alt="Download Icon"
                        className="h-5 w-5"
                      />
                      <span className="text-[#30bd7e]">
                        <span>Download PDF</span>
                        <br />
                        <small>ReSet</small>
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {/* Doctor's Letter End*/}
        </div>

        <div className="flex justify-center lg:justify-end items-center mt-8 mb-4">
          <button
            className="bg-[#30bd7e] text-white px-4 py-2 rounded-md transition-colors"
            onClick={resetForm}
          >
            Reset form
          </button>
        </div>
      </div>
    </div>
  );
}

export default Main;
